import Vue from 'vue'
import VueI18n from 'vue-i18n'

import ar from '@/locales/ar.json'
import en from '@/locales/en.json'
import ur from '@/locales/ur.json'

import {
  DEFAULT_LOCALE,
  LOCALE_CODES,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  applyDocumentLocale,
  getLocaleConfig,
  isRTLLocale,
  isSupportedLocale,
  persistLocale,
  resolveInitialLocale,
} from './locales'

Vue.use(VueI18n)

const messages = { en, ur, ar }

const isDev = process.env.NODE_ENV === 'development'

const dateTimeFormats = LOCALE_CODES.reduce((acc, code) => {
  acc[code] = {
    short: { year: 'numeric', month: 'short', day: 'numeric' },
    long: {
      year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
    },
    time: { hour: 'numeric', minute: 'numeric' },
    dateTime: {
      year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric',
    },
  }
  return acc
}, {})

const numberFormats = LOCALE_CODES.reduce((acc, code) => {
  acc[code] = {
    decimal: { style: 'decimal', maximumFractionDigits: 2 },
    // Packages and subscriptions are both priced and charged in USD.
    currency: {
      style: 'currency', currency: 'USD', currencyDisplay: 'code', minimumFractionDigits: 2,
    },
    percent: { style: 'percent', maximumFractionDigits: 1 },
  }
  return acc
}, {})

const initialLocale = resolveInitialLocale()

const i18n = new VueI18n({
  locale: initialLocale,
  // Anything not yet translated renders the English copy instead of the raw key.
  fallbackLocale: DEFAULT_LOCALE,
  messages,
  dateTimeFormats,
  numberFormats,
  // Surface missing keys while developing, stay quiet in production builds.
  silentTranslationWarn: !isDev,
  silentFallbackWarn: !isDev,
  // Intl formatters are resolved with the locale's full BCP-47 tag.
  formatFallbackMessages: true,
})

// Make sure <html lang/dir> matches the locale we booted with, before first paint.
applyDocumentLocale(initialLocale)

/**
 * The one supported way to change language at runtime.
 * Updates the vue-i18n instance, persists the choice and syncs <html lang/dir>.
 *
 * @param {string} code a supported locale code
 * @returns {string} the locale that is now active
 */
export const setLocale = code => {
  const next = isSupportedLocale(code) ? code : DEFAULT_LOCALE

  if (i18n.locale !== next) i18n.locale = next
  persistLocale(next)
  applyDocumentLocale(next)

  // Some third-party widget config (e.g. the MoEngage Cards notification
  // panel — see src/plugins/moengage.js) is only read once, at app boot, and
  // isn't reactive to a live locale change. Re-running that init in place
  // was tried and confirmed NOT to refresh the already-mounted widget, so a
  // full reload is the only thing that actually re-localizes it (the locale
  // is already persisted above, so the reload boots straight into `next`).
  if (typeof window !== 'undefined') {
    window.location.reload()
  }

  return next
}

/** Intl tag (e.g. `ar-AE`) for the active locale — for moment/Intl formatting. */
export const currentIntlTag = () => getLocaleConfig(i18n.locale).intlTag

export {
  DEFAULT_LOCALE,
  LOCALE_CODES,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  applyDocumentLocale,
  getLocaleConfig,
  isRTLLocale,
  isSupportedLocale,
  resolveInitialLocale,
}

export default i18n
